import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { SalesPortalRestaurantEmployeesDocument } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-restaurant-employees.generated';
import { useAlertContext } from '@usepepper/sprout-kit-ui';
import { useCallback } from 'react';
import { useExperienceToasts } from '@experiences/hooks/use-experience-toasts';
import { useSalesPortalRemoveEmployeeMutation } from '@sales-portal/domain/accounts/screens/employee-list/blocs/documents/sales-portal-remove-employee.generated';

interface UseRemoveEmployeeMutatorParams {
  employee: RestaurantEmployee | null;
  /**
   * Fires after a successful removal with the id of the employee that was
   * just removed.
   */
  onRemoved: (removedEmployeeId: string) => void;
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseRemoveEmployeeMutatorResult {
  onRemovePress: () => void;
  removing: boolean;
}

export const useRemoveEmployeeMutator = ({
  employee,
  onRemoved,
  restaurantUUID,
  supplierUUID,
}: UseRemoveEmployeeMutatorParams): UseRemoveEmployeeMutatorResult => {
  const { alert } = useAlertContext('employee_edit_drawer_remove');
  const { showErrorToast, showToast } = useExperienceToasts();
  const [removeEmployee, { loading: removing }] =
    useSalesPortalRemoveEmployeeMutation();

  const onRemovePress = useCallback(() => {
    if (!employee || removing) {
      return;
    }
    const { employee_id: employeeId, employee_name: employeeName } = employee;
    alert({
      buttons: [
        {
          action: () => {},
          style: 'cancel',
          title: i18n.t('cancel'),
        },
        {
          action: async () => {
            try {
              const { data } = await removeEmployee({
                awaitRefetchQueries: true,
                refetchQueries: [SalesPortalRestaurantEmployeesDocument],
                variables: {
                  businessUUID: restaurantUUID,
                  employeeUUID: employeeId,
                  supplierUUID,
                },
              });
              if (!data?.removeEmployeeFromBusiness?.employee_id) {
                captureException(
                  new Error(
                    'removeEmployeeFromBusiness returned no employee_id',
                  ),
                  { extra: { response: data?.removeEmployeeFromBusiness } },
                );
                showErrorToast();
                return;
              }
              onRemoved(employeeId);
              showToast({
                title: i18n.t(
                  'sales_portal.employee_edit_drawer.remove_success',
                  { employeeName },
                ),
                variant: 'success',
              });
            } catch (error) {
              captureException(error);
              showErrorToast();
            }
          },
          style: 'destructive',
          title: i18n.t('sales_portal.employee_edit_drawer.remove'),
        },
      ],
      message: i18n.t(
        'sales_portal.employee_edit_drawer.remove_confirm.message',
        { employeeName },
      ),
      title: i18n.t('sales_portal.employee_edit_drawer.remove_confirm.title', {
        employeeName,
      }),
    });
  }, [
    alert,
    employee,
    onRemoved,
    removeEmployee,
    removing,
    restaurantUUID,
    showErrorToast,
    showToast,
    supplierUUID,
  ]);

  return { onRemovePress, removing };
};
